import { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert, Autocomplete, Box, Button, Card, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, FormControl,
    FormControlLabel, Grid, IconButton, InputAdornment, InputLabel, LinearProgress, Link, MenuItem, Select, Stack, Switch, Table, TableBody,
    TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@mui/material";
import { alpha, useTheme } from "@mui/material/styles";
import { Add, Delete, Download, Edit, EditCalendar, FileDownload, Flag, OpenInNew, PersonAdd, PostAdd, Replay } from "@mui/icons-material";
import { baseData, getData, postData } from "../../services/base.service";
import { downloadBlob } from "../../services/projectFiles.service";
import { useNotification } from "../Shared/NotificationProvider";
import { BillingPeriod, BillingPosition, BudgetLine, ContractPeriod, Milestone, ProjectBilling, Quantities } from "../../types/billingTypes";
import { formatMoney, numberOrNull } from "./projectUtils";
import GizTimesheetDialog from "./GizTimesheetDialog";

type ProjectBillingTabProps = {
    projectId: number;
    isEditMode: boolean;
    setLoading: Function;
};

const fmtDays = (value: number) => (Number.isInteger(value) ? String(value) : value.toFixed(1));
const ddmmyyyy = (iso: string | null) => (iso ? `${iso.slice(8, 10)}.${iso.slice(5, 7)}.${iso.slice(0, 4)}` : '-');
const daysOf = (q: Quantities) => q.days_domestic + q.days_abroad;

function Kpi({ label, value, sub, tone }: { label: string; value: string; sub?: string; tone?: 'warning' | 'error' | 'success' }) {
    return (
        <Card variant="outlined" sx={{ height: '100%', borderRadius: 2 }}>
            <Box sx={{ padding: 1.5 }}>
                <Typography variant="body2" color="text.secondary">{label}</Typography>
                <Typography variant="h5" sx={{ fontWeight: 700, color: tone ? `${tone}.main` : 'text.primary' }}>{value}</Typography>
                {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
            </Box>
        </Card>
    );
}

// ---------- dialogs ----------

type PositionForm = {
    bpos: string; title: string; group_label: string; timesheet_role: string; is_pool: boolean; fee_rate: string; per_diem_rate: string;
    accommodation_rate: string; planned_days_domestic: string; planned_days_abroad: string;
};

const emptyPosition = (bpos: number): PositionForm => ({
    bpos: String(bpos), title: '', group_label: '', timesheet_role: '', is_pool: false, fee_rate: '', per_diem_rate: '', accommodation_rate: '',
    planned_days_domestic: '', planned_days_abroad: '',
});

const positionToForm = (p: BillingPosition): PositionForm => ({
    bpos: String(p.bpos), title: p.title, group_label: p.group_label ?? '', timesheet_role: p.timesheet_role ?? '', is_pool: p.is_pool,
    fee_rate: p.fee_rate?.toString() ?? '', per_diem_rate: p.per_diem_rate?.toString() ?? '', accommodation_rate: p.accommodation_rate?.toString() ?? '',
    planned_days_domestic: p.planned_days_domestic?.toString() ?? '', planned_days_abroad: p.planned_days_abroad?.toString() ?? '',
});

function PositionDialog({ open, initial, onClose, onSave }: { open: boolean; initial: PositionForm | null; onClose: () => void; onSave: (form: PositionForm) => void }) {
    const [form, setForm] = useState<PositionForm | null>(initial);
    useEffect(() => { setForm(initial); }, [initial]);
    if (!form) return null;
    const set = (key: keyof PositionForm, value: string | boolean) => setForm({ ...form, [key]: value });
    const numberField = (key: keyof PositionForm, label: string, unit?: string) => (
        <TextField fullWidth size="small" type="number" label={label} value={form[key] as string} onChange={(e) => set(key, e.target.value)}
            InputProps={unit ? { endAdornment: <InputAdornment position="end">{unit}</InputAdornment> } : undefined} />
    );
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>{initial?.title ? 'Edit position' : 'Add position'}</DialogTitle>
            <DialogContent>
                <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                    <Grid item xs={3}>{numberField('bpos', 'BPos')}</Grid>
                    <Grid item xs={9}><TextField fullWidth size="small" required label="Position" placeholder="Sen. IT expert (IT)" value={form.title} onChange={(e) => set('title', e.target.value)} /></Grid>
                    <Grid item xs={12} md={7}><TextField fullWidth size="small" label="Group" placeholder="Kurzzeitkräfte Pool Int." value={form.group_label} onChange={(e) => set('group_label', e.target.value)} /></Grid>
                    <Grid item xs={12} md={5}><FormControlLabel control={<Switch checked={form.is_pool} onChange={(e) => set('is_pool', e.target.checked)} />} label="Pool of experts" /></Grid>
                    <Grid item xs={12}><TextField fullWidth size="small" label="Role on the GIZ timesheet" placeholder="Senior IT Expert (defaults to the position)" value={form.timesheet_role} onChange={(e) => set('timesheet_role', e.target.value)} /></Grid>
                    <Grid item xs={6}>{numberField('planned_days_domestic', 'Planned days domestic (Inland)', 'days')}</Grid>
                    <Grid item xs={6}>{numberField('planned_days_abroad', 'Planned days abroad (Ausland)', 'days')}</Grid>
                    <Grid item xs={4}>{numberField('fee_rate', 'Fee rate 5.4', '€/day')}</Grid>
                    <Grid item xs={4}>{numberField('per_diem_rate', 'Per diem 5.6', '€')}</Grid>
                    <Grid item xs={4}>{numberField('accommodation_rate', 'Accommodation 5.7', '€')}</Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disableElevation disabled={!form.title.trim()} onClick={() => onSave(form)}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

type PersonOption = { name: string; email: string; staffOid: string | null; expertID: number | null; group: string };

function MemberDialog({ open, onClose, onSave }: { open: boolean; onClose: () => void; onSave: (person: PersonOption) => void }) {
    const [staff, setStaff] = useState<PersonOption[]>([]);
    const [experts, setExperts] = useState<PersonOption[]>([]);
    const [value, setValue] = useState<PersonOption | string | null>(null);

    useEffect(() => {
        if (!open) return;
        setValue(null);
        setExperts([]);
        getData('projects/staff/directory').then((res) => setStaff([
            ...res.people.map((p: { oid: string; name: string; email: string }) => ({ name: p.name, email: p.email, staffOid: p.oid, expertID: null, group: 'Internal staff' })),
        ])).catch(() => setStaff([]));
    }, [open]);

    const searchExperts = async (query: string) => {
        if (query.trim().length < 2) { setExperts([]); return; }
        const res = await getData(`experts/availableforreference/0?search=${encodeURIComponent(query.trim())}`).catch(() => []);
        setExperts((res as { expertID: number; name: string; email: string }[]).map((e) => ({ name: e.name, email: e.email, staffOid: null, expertID: e.expertID, group: 'Experts database' })));
    };

    const resolved: PersonOption | null = typeof value === 'string'
        ? (value.trim() ? { name: value.trim(), email: '', staffOid: null, expertID: null, group: '' } : null)
        : value;

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
            <DialogTitle>Add member</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Internal staff can generate their GIZ timesheet from the internal timesheet; experts from the database or a typed name are billed by hand.
                </Typography>
                <Autocomplete freeSolo sx={{ marginTop: 1.5 }} options={[...staff, ...experts]} groupBy={(o) => o.group} value={value}
                    onChange={(_, v) => setValue(v)}
                    onInputChange={(_, v, reason) => { if (reason === 'input') { setValue(v); searchExperts(v); } }}
                    getOptionLabel={(o) => (typeof o === 'string' ? o : `${o.name}${o.email ? ` (${o.email})` : ''}`)}
                    renderInput={(params) => <TextField {...params} autoFocus label="Person" placeholder="Name, internal staff or expert" />} />
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disableElevation disabled={!resolved} onClick={() => resolved && onSave(resolved)}>Add</Button>
            </DialogActions>
        </Dialog>
    );
}

function QuantitiesDialog({ target, onClose, onSave }: {
    target: { position: BillingPosition; billingPeriod: BillingPeriod; manual: Quantities | null; timesheetCount: number } | null;
    onClose: () => void; onSave: (q: Quantities) => void;
}) {
    const [form, setForm] = useState({ days_domestic: '', days_abroad: '', per_diem_qty: '', accommodation_qty: '' });
    useEffect(() => {
        const m = target?.manual;
        if (target) setForm({
            days_domestic: m?.days_domestic ? String(m.days_domestic) : '', days_abroad: m?.days_abroad ? String(m.days_abroad) : '',
            per_diem_qty: m?.per_diem_qty ? String(m.per_diem_qty) : '', accommodation_qty: m?.accommodation_qty ? String(m.accommodation_qty) : '',
        });
    }, [target]);
    if (!target) return null;
    const field = (key: keyof typeof form, label: string) => (
        <TextField fullWidth size="small" type="number" label={label} value={form[key]} onChange={(e) => setForm({ ...form, [key]: e.target.value })} />
    );
    return (
        <Dialog open onClose={onClose} fullWidth maxWidth="xs">
            <DialogTitle>BPos {target.position.bpos} · Abr.per {ddmmyyyy(target.billingPeriod.end_date)}</DialogTitle>
            <DialogContent>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                    Manual quantities, e.g. for external experts who send their own timesheet. They replace earlier manual values;
                    {target.timesheetCount ? ` ${target.timesheetCount} generated GIZ timesheet(s) are added on top automatically.` : ' generated GIZ timesheets are added on top automatically.'}
                </Typography>
                <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                    <Grid item xs={6}>{field('days_domestic', 'Days domestic')}</Grid>
                    <Grid item xs={6}>{field('days_abroad', 'Days abroad')}</Grid>
                    <Grid item xs={6}>{field('per_diem_qty', 'Per diem')}</Grid>
                    <Grid item xs={6}>{field('accommodation_qty', 'Nights')}</Grid>
                </Grid>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Cancel</Button>
                <Button variant="contained" disableElevation onClick={() => onSave({
                    days_domestic: numberOrNull(form.days_domestic) ?? 0, days_abroad: numberOrNull(form.days_abroad) ?? 0,
                    per_diem_qty: numberOrNull(form.per_diem_qty) ?? 0, accommodation_qty: numberOrNull(form.accommodation_qty) ?? 0,
                })}>Save</Button>
            </DialogActions>
        </Dialog>
    );
}

// ---------- tab ----------

function ProjectBillingTab({ projectId, isEditMode, setLoading }: ProjectBillingTabProps) {
    const theme = useTheme();
    const notify = useNotification();
    const [billing, setBilling] = useState<ProjectBilling | null>(null);
    const [periodId, setPeriodId] = useState<number | null>(null);
    const [periodDialog, setPeriodDialog] = useState<{ id?: number; name: string; start_date: string; end_date: string; copyFrom: number | '' } | null>(null);
    const [positionDialog, setPositionDialog] = useState<{ id?: number; form: PositionForm } | null>(null);
    const [memberPositionId, setMemberPositionId] = useState<number | null>(null);
    const [billingPeriodDialog, setBillingPeriodDialog] = useState<{ end_date: string; label: string } | null>(null);
    const [quantitiesTarget, setQuantitiesTarget] = useState<Parameters<typeof QuantitiesDialog>[0]['target']>(null);
    const [gizDialog, setGizDialog] = useState<{ memberId?: number; month?: string } | null>(null);
    const [lineDialog, setLineDialog] = useState<{ id?: number; vpos: string; label: string; unit_price: string; planned_qty: string; planned_amount: string } | null>(null);
    const [lineBillingTarget, setLineBillingTarget] = useState<{ line: BudgetLine; billingPeriod: BillingPeriod; qty: string; amount: string } | null>(null);
    const [milestoneDialog, setMilestoneDialog] = useState<{ id?: number; name: string; amount: string; currency: string; due_date: string; status: Milestone['status']; comment: string } | null>(null);

    const load = useCallback(async () => {
        try {
            const result = await getData(`projects/${projectId}/billing`) as ProjectBilling;
            setBilling(result);
            setPeriodId((current) => (current && result.periods.some((p) => p.ID === current) ? current : result.periods[result.periods.length - 1]?.ID ?? null));
        } catch (error) {
            notify(`Couldn't load the billing: ${(error as Error).message}`, 'error');
        }
    }, [projectId, notify]);

    useEffect(() => { load(); }, [load]);

    const period: ContractPeriod | null = useMemo(() => billing?.periods.find((p) => p.ID === periodId) ?? null, [billing, periodId]);
    const periodTimesheets = useMemo(() => {
        const positionIds = new Set(period?.positions.map((p) => p.ID) ?? []);
        return (billing?.timesheets ?? []).filter((t) => t.positionId !== null && positionIds.has(t.positionId));
    }, [billing, period]);

    const run = async (work: () => Promise<unknown>, success?: string) => {
        setLoading(true);
        try {
            await work();
            if (success) notify(success, 'success');
            await load();
        } catch (error) {
            notify((error as Error).message, 'error');
        } finally {
            setLoading(false);
        }
    };

    const savePosition = (form: PositionForm) => {
        const payload = {
            bpos: numberOrNull(form.bpos) ?? undefined,
            title: form.title.trim(),
            group_label: form.group_label.trim() || null,
            timesheet_role: form.timesheet_role.trim() || null,
            is_pool: form.is_pool,
            fee_rate: numberOrNull(form.fee_rate),
            per_diem_rate: numberOrNull(form.per_diem_rate),
            accommodation_rate: numberOrNull(form.accommodation_rate),
            planned_days_domestic: numberOrNull(form.planned_days_domestic),
            planned_days_abroad: numberOrNull(form.planned_days_abroad),
        };
        const id = positionDialog?.id;
        run(() => (id
            ? postData(`projects/${projectId}/billing/positions/update`, { id, ...payload })
            : postData(`projects/${projectId}/billing/positions/add`, { contractPeriodId: periodId, ...payload })), 'Position saved.');
        setPositionDialog(null);
    };

    const downloadTimesheet = async (id: number, month: string, name: string) => {
        try {
            const blob = await baseData(`projects/${projectId}/billing/giz-timesheets/${id}/file`, 'GET', undefined, 'blob') as Blob;
            const initials = name.split(/[\s-]+/).filter(Boolean).map((w) => w[0].toUpperCase()).join('');
            downloadBlob(blob, `TS ${month} ${initials}.xlsx`);
        } catch (error) {
            notify(`Couldn't download: ${(error as Error).message}`, 'error');
        }
    };

    const exportProjectSheet = async () => {
        try {
            const blob = await baseData(`projects/${projectId}/billing/project-sheet`, 'GET', undefined, 'blob') as Blob;
            downloadBlob(blob, `Projektsheet ${billing?.project.project_number ?? projectId} ${new Date().toISOString().slice(0, 10)}.xlsx`);
        } catch (error) {
            notify(`Couldn't export: ${(error as Error).message}`, 'error');
        }
    };

    if (!billing) return <Box display="flex" justifyContent="center" paddingY={6}><CircularProgress /></Box>;

    const totals = period?.totals;
    const openTone = totals && totals.plannedDays > 0
        ? (totals.remainingDays < 0 ? 'error' : totals.remainingDays / totals.plannedDays < 0.1 ? 'warning' : 'success')
        : undefined;
    const currency = billing.project.currency || 'EUR';

    return (
        <Box paddingTop={2}>
            {billing.project.billing_type === 'lump_sum' && (
                <Alert severity="info" sx={{ marginBottom: 2 }}>This project is billed as a lump sum - positions and expert-days are optional here.</Alert>
            )}

            {/* Contract period selector */}
            <Stack direction="row" alignItems="center" gap={1.5} flexWrap="wrap" sx={{ marginBottom: 2 }}>
                {billing.periods.length > 0 ? (
                    <FormControl size="small" sx={{ minWidth: 260 }}>
                        <InputLabel>Contract period</InputLabel>
                        <Select label="Contract period" value={periodId ?? ''} onChange={(e) => setPeriodId(Number(e.target.value))}>
                            {billing.periods.map((p) => (
                                <MenuItem key={p.ID} value={p.ID}>{p.name}{p.start_date || p.end_date ? ` · ${ddmmyyyy(p.start_date)} – ${ddmmyyyy(p.end_date)}` : ''}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                ) : (
                    <Typography color="text.secondary">No cost plan yet. {isEditMode ? 'Start with the current contract period (VE).' : 'Switch to edit mode to add one.'}</Typography>
                )}
                {isEditMode && (
                    <>
                        <Button size="small" variant="outlined" startIcon={<Add />}
                            onClick={() => setPeriodDialog({ name: '', start_date: '', end_date: '', copyFrom: period?.ID ?? '' })}>Contract period</Button>
                        {period && (
                            <Button size="small" startIcon={<Edit />} onClick={() => setPeriodDialog({ id: period.ID, name: period.name, start_date: period.start_date ?? '', end_date: period.end_date ?? '', copyFrom: '' })}>
                                Edit period
                            </Button>
                        )}
                    </>
                )}
                <Box sx={{ flexGrow: 1 }} />
                {billing.periods.length > 0 && (
                    <Button size="small" variant="outlined" startIcon={<FileDownload />} onClick={exportProjectSheet}>Export project sheet</Button>
                )}
                {period && period.positions.some((p) => p.members.length) && (
                    <Button size="small" variant="contained" disableElevation startIcon={<PostAdd />} onClick={() => setGizDialog({})}>GIZ timesheet</Button>
                )}
            </Stack>

            {period && totals && (
                <>
                    <Grid container spacing={1.5} sx={{ marginBottom: 2.5 }}>
                        <Grid item xs={6} md={3}><Kpi label="Days still open" value={fmtDays(totals.remainingDays)} tone={openTone}
                            sub={totals.plannedDays ? `${Math.round((totals.remainingDays / totals.plannedDays) * 100)} % of ${fmtDays(totals.plannedDays)} planned` : 'no days planned'} /></Grid>
                        <Grid item xs={6} md={3}><Kpi label="Billed days" value={fmtDays(totals.billedDays)} sub="in billing periods" /></Grid>
                        <Grid item xs={6} md={3}><Kpi label="Draft days" value={fmtDays(totals.draftedDays)} sub="timesheets not yet billed" /></Grid>
                        <Grid item xs={6} md={3}><Kpi label="Billed in total" value={formatMoney(totals.billedTotal, currency)}
                            sub={`of ${formatMoney(totals.plannedTotal, currency)} · fees ${formatMoney(totals.billedFee, currency)} of ${formatMoney(totals.plannedFee, currency)}`} /></Grid>
                    </Grid>

                    {/* Positions */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginBottom: 1 }}>
                        <Typography variant="h6">Positions</Typography>
                        {isEditMode && (
                            <Button size="small" variant="outlined" startIcon={<Add />}
                                onClick={() => setPositionDialog({ form: emptyPosition(period.positions.reduce((m, p) => Math.max(m, p.bpos), 0) + 1) })}>Position</Button>
                        )}
                    </Stack>
                    {period.positions.length === 0 ? (
                        <Typography color="text.secondary" sx={{ paddingY: 2 }}>No positions in this contract period yet.</Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 50 }}>BPos</TableCell>
                                        <TableCell>Position and members</TableCell>
                                        <TableCell align="right">Planned</TableCell>
                                        <TableCell align="right">Rate</TableCell>
                                        <TableCell align="right">Billed</TableCell>
                                        <TableCell align="right">Drafts</TableCell>
                                        <TableCell sx={{ minWidth: 150 }}>Open days</TableCell>
                                        {isEditMode && <TableCell />}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {period.positions.map((p) => {
                                        const used = p.plannedDays > 0 ? Math.min(100, ((p.plannedDays - p.remainingDays) / p.plannedDays) * 100) : 0;
                                        const tone = p.remainingDays < 0 ? theme.palette.error.main : p.plannedDays && p.remainingDays / p.plannedDays < 0.1 ? theme.palette.warning.main : theme.palette.primary.main;
                                        return (
                                            <TableRow key={p.ID} hover>
                                                <TableCell sx={{ fontWeight: 600 }}>{p.bpos}</TableCell>
                                                <TableCell>
                                                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{p.title}</Typography>
                                                    {(p.group_label || p.is_pool) && (
                                                        <Typography variant="caption" color="text.secondary">{[p.group_label, p.is_pool ? 'pool' : ''].filter(Boolean).join(' · ')}</Typography>
                                                    )}
                                                    <Stack direction="row" gap={0.5} flexWrap="wrap" sx={{ marginTop: 0.5 }}>
                                                        {p.members.map((m) => (
                                                            <Chip key={m.ID} size="small" label={m.member_name} variant={m.staff_oid ? 'filled' : 'outlined'}
                                                                title={m.staff_oid ? 'Linked to the internal timesheet' : m.expertID ? 'Expert from the database' : 'Name only'}
                                                                onDelete={isEditMode ? () => window.confirm(`Remove ${m.member_name}?`) && run(() => postData(`projects/${projectId}/billing/members/remove`, { id: m.ID })) : undefined} />
                                                        ))}
                                                        {isEditMode && <Chip size="small" icon={<PersonAdd />} label="Member" variant="outlined" color="primary" onClick={() => setMemberPositionId(p.ID)} />}
                                                    </Stack>
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title={`${fmtDays(p.planned_days_domestic ?? 0)} domestic · ${fmtDays(p.planned_days_abroad ?? 0)} abroad`}><span>{fmtDays(p.plannedDays)}</span></Tooltip>
                                                </TableCell>
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <Tooltip title={`Per diem ${p.per_diem_rate ?? '-'} € · accommodation ${p.accommodation_rate ?? '-'} €`}>
                                                        <span>{p.fee_rate !== null ? `${p.fee_rate} €` : '-'}</span>
                                                    </Tooltip>
                                                </TableCell>
                                                <TableCell align="right">{fmtDays(daysOf(p.billed))}</TableCell>
                                                <TableCell align="right" sx={{ color: daysOf(p.drafted) ? 'text.primary' : 'text.disabled' }}>{fmtDays(daysOf(p.drafted))}</TableCell>
                                                <TableCell>
                                                    <Stack direction="row" alignItems="center" gap={1}>
                                                        <Typography variant="body2" sx={{ fontWeight: 700, minWidth: 36, color: p.remainingDays < 0 ? 'error.main' : 'text.primary' }}>{fmtDays(p.remainingDays)}</Typography>
                                                        <LinearProgress variant="determinate" value={used} sx={{ flexGrow: 1, height: 6, borderRadius: 3, backgroundColor: alpha(tone, 0.15),
                                                            '& .MuiLinearProgress-bar': { backgroundColor: tone, borderRadius: 3 } }} />
                                                    </Stack>
                                                </TableCell>
                                                {isEditMode && (
                                                    <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                        <IconButton size="small" onClick={() => setPositionDialog({ id: p.ID, form: positionToForm(p) })}><Edit fontSize="small" /></IconButton>
                                                        <IconButton size="small" color="error" onClick={() => window.confirm(`Remove BPos ${p.bpos} ${p.title}?`) && run(() => postData(`projects/${projectId}/billing/positions/remove`, { id: p.ID }))}>
                                                            <Delete fontSize="small" />
                                                        </IconButton>
                                                    </TableCell>
                                                )}
                                            </TableRow>
                                        );
                                    })}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {/* Billing periods */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginTop: 4, marginBottom: 1 }}>
                        <Typography variant="h6">Billing periods</Typography>
                        {isEditMode && <Button size="small" variant="outlined" startIcon={<EditCalendar />} onClick={() => setBillingPeriodDialog({ end_date: '', label: '' })}>Billing period</Button>}
                    </Stack>
                    {period.billingPeriods.length === 0 ? (
                        <Typography color="text.secondary" sx={{ paddingY: 1 }}>No billing periods yet{isEditMode ? '' : ' - add them in edit mode'}.</Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>BPos</TableCell>
                                        {period.billingPeriods.map((bp) => (
                                            <TableCell key={bp.ID} align="center" sx={{ whiteSpace: 'nowrap' }}>
                                                {bp.label || ddmmyyyy(bp.end_date)}
                                                <Box>
                                                    <Chip size="small" label={bp.status === 'invoiced' ? 'invoiced' : 'open'} color={bp.status === 'invoiced' ? 'default' : 'primary'} variant="outlined"
                                                        onClick={isEditMode ? () => run(() => postData(`projects/${projectId}/billing/billing-periods/update`, { id: bp.ID, status: bp.status === 'open' ? 'invoiced' : 'open' })) : undefined} />
                                                    {isEditMode && (
                                                        <IconButton size="small" onClick={() => window.confirm('Remove this billing period? Its timesheets become drafts again.') && run(() => postData(`projects/${projectId}/billing/billing-periods/remove`, { id: bp.ID }))}>
                                                            <Delete sx={{ fontSize: 16 }} />
                                                        </IconButton>
                                                    )}
                                                </Box>
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {period.positions.map((p) => (
                                        <TableRow key={p.ID}>
                                            <TableCell sx={{ whiteSpace: 'nowrap' }}>{p.bpos} <Typography component="span" variant="caption" color="text.secondary">{p.title}</Typography></TableCell>
                                            {period.billingPeriods.map((bp) => {
                                                const cell = p.perPeriod.find((c) => c.billingPeriodId === bp.ID);
                                                const days = cell ? daysOf(cell.quantities) : 0;
                                                return (
                                                    <TableCell key={bp.ID} align="center"
                                                        onClick={isEditMode && cell ? () => setQuantitiesTarget({ position: p, billingPeriod: bp, manual: cell.manual, timesheetCount: cell.timesheetCount }) : undefined}
                                                        sx={{ cursor: isEditMode ? 'pointer' : 'default', color: days ? 'text.primary' : 'text.disabled', '&:hover': isEditMode ? { backgroundColor: 'action.hover' } : undefined }}>
                                                        <Tooltip title={cell && days ? `${fmtDays(cell.quantities.days_domestic)} domestic · ${fmtDays(cell.quantities.days_abroad)} abroad · ${cell.quantities.per_diem_qty} per diem · ${cell.quantities.accommodation_qty} nights${cell.timesheetCount ? ` · ${cell.timesheetCount} GIZ timesheet(s)` : ''}${cell.hasManual ? ' · manual entry' : ''}` : ''}>
                                                            <span>{days ? fmtDays(days) : '–'}{cell?.hasManual ? '*' : ''}</span>
                                                        </Tooltip>
                                                    </TableCell>
                                                );
                                            })}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                            <Typography variant="caption" color="text.secondary">Days per billing period; * = includes manual quantities. {isEditMode ? 'Click a cell to enter manual quantities.' : ''}</Typography>
                        </Box>
                    )}

                    {/* Other cost lines */}
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginTop: 4, marginBottom: 1 }}>
                        <Typography variant="h6">Other costs</Typography>
                        {isEditMode && (
                            <Button size="small" variant="outlined" startIcon={<Add />}
                                onClick={() => setLineDialog({ vpos: '', label: '', unit_price: '', planned_qty: '', planned_amount: '' })}>Cost line</Button>
                        )}
                    </Stack>
                    {period.budgetLines.length === 0 ? (
                        <Typography color="text.secondary" sx={{ paddingY: 1 }}>No other cost lines (flights, travel, office, other costs, flexible fee).</Typography>
                    ) : (
                        <Box sx={{ overflowX: 'auto' }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell>VPos</TableCell>
                                        <TableCell>Cost line</TableCell>
                                        <TableCell align="right">Planned</TableCell>
                                        {period.billingPeriods.map((bp) => <TableCell key={bp.ID} align="right" sx={{ whiteSpace: 'nowrap' }}>{bp.label || ddmmyyyy(bp.end_date)}</TableCell>)}
                                        <TableCell align="right">Open</TableCell>
                                        {isEditMode && <TableCell />}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {period.budgetLines.map((line) => (
                                        <TableRow key={line.ID} hover>
                                            <TableCell>{line.vpos}</TableCell>
                                            <TableCell>
                                                {line.label}
                                                {line.unit_price !== null && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{line.planned_qty ?? 0} x {formatMoney(line.unit_price)}</Typography>}
                                            </TableCell>
                                            <TableCell align="right">{formatMoney(line.plannedAmount)}</TableCell>
                                            {period.billingPeriods.map((bp) => {
                                                const c = line.perPeriod.find((x) => x.billingPeriodId === bp.ID);
                                                return (
                                                    <TableCell key={bp.ID} align="right"
                                                        onClick={isEditMode ? () => setLineBillingTarget({ line, billingPeriod: bp, qty: c?.qty?.toString() ?? '', amount: c?.hasEntry && c.qty === null ? String(c.amount) : '' }) : undefined}
                                                        sx={{ cursor: isEditMode ? 'pointer' : 'default', color: c?.amount ? 'text.primary' : 'text.disabled', '&:hover': isEditMode ? { backgroundColor: 'action.hover' } : undefined }}>
                                                        {c?.amount ? formatMoney(c.amount) : '–'}
                                                    </TableCell>
                                                );
                                            })}
                                            <TableCell align="right" sx={{ fontWeight: 600, color: line.remainingAmount < 0 ? 'error.main' : 'text.primary' }}>{formatMoney(line.remainingAmount)}</TableCell>
                                            {isEditMode && (
                                                <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                    <IconButton size="small" onClick={() => setLineDialog({
                                                        id: line.ID, vpos: line.vpos ?? '', label: line.label, unit_price: line.unit_price?.toString() ?? '',
                                                        planned_qty: line.planned_qty?.toString() ?? '', planned_amount: line.planned_amount?.toString() ?? '',
                                                    })}><Edit fontSize="small" /></IconButton>
                                                    <IconButton size="small" color="error" onClick={() => window.confirm(`Remove ${line.label}?`) && run(() => postData(`projects/${projectId}/billing/budget-lines/remove`, { id: line.ID }))}>
                                                        <Delete fontSize="small" />
                                                    </IconButton>
                                                </TableCell>
                                            )}
                                        </TableRow>
                                    ))}
                                </TableBody>
                            </Table>
                        </Box>
                    )}

                    {/* GIZ timesheets */}
                    <Typography variant="h6" sx={{ marginTop: 4, marginBottom: 1 }}>GIZ timesheets</Typography>
                    {periodTimesheets.length === 0 ? (
                        <Typography color="text.secondary" sx={{ paddingY: 1 }}>None generated yet.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Month</TableCell>
                                    <TableCell>Expert</TableCell>
                                    <TableCell>BPos</TableCell>
                                    <TableCell align="right">Days</TableCell>
                                    <TableCell align="right">Per diem / nights</TableCell>
                                    <TableCell sx={{ minWidth: 190 }}>Billing period</TableCell>
                                    <TableCell align="right" />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {periodTimesheets.map((t) => (
                                    <TableRow key={t.ID} hover>
                                        <TableCell>{t.month}</TableCell>
                                        <TableCell>{t.expertName}<Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{t.role}</Typography></TableCell>
                                        <TableCell>{t.bpos}</TableCell>
                                        <TableCell align="right">{fmtDays(t.days)}</TableCell>
                                        <TableCell align="right">{t.per_diem_qty} / {t.accommodation_qty}</TableCell>
                                        <TableCell>
                                            <Select size="small" variant="standard" fullWidth value={t.billingPeriodId ?? ''} displayEmpty
                                                onChange={(e) => run(() => postData(`projects/${projectId}/billing/giz-timesheets/assign`, { id: t.ID, billingPeriodId: e.target.value === '' ? null : Number(e.target.value) }))}>
                                                <MenuItem value="">Draft (not billed)</MenuItem>
                                                {period.billingPeriods.map((bp) => <MenuItem key={bp.ID} value={bp.ID}>{bp.label || `Abr.per ${ddmmyyyy(bp.end_date)}`}</MenuItem>)}
                                            </Select>
                                        </TableCell>
                                        <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                            {t.fileUrl && <Tooltip title="Open in SharePoint"><IconButton size="small" component={Link} href={t.fileUrl} target="_blank"><OpenInNew fontSize="small" /></IconButton></Tooltip>}
                                            <Tooltip title="Download"><IconButton size="small" onClick={() => downloadTimesheet(t.ID, t.month, t.expertName)}><Download fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Review and generate again"><IconButton size="small" onClick={() => setGizDialog({ memberId: t.memberId, month: t.month })}><Replay fontSize="small" /></IconButton></Tooltip>
                                            {isEditMode && (
                                                <Tooltip title="Delete"><IconButton size="small" color="error" onClick={() => window.confirm(`Delete the GIZ timesheet ${t.month} of ${t.expertName}?`) && run(() => postData(`projects/${projectId}/billing/giz-timesheets/remove`, { id: t.ID }))}>
                                                    <Delete fontSize="small" />
                                                </IconButton></Tooltip>
                                            )}
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    )}
                </>
            )}

            {(billing.project.billing_type === 'lump_sum' || billing.milestones.length > 0) && (
                <>
                    <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ marginTop: 4, marginBottom: 1 }}>
                        <Typography variant="h6">Milestones (lump sum)</Typography>
                        {isEditMode && (
                            <Button size="small" variant="outlined" startIcon={<Flag />}
                                onClick={() => setMilestoneDialog({ name: '', amount: '', currency: currency, due_date: '', status: 'planned', comment: '' })}>Milestone</Button>
                        )}
                    </Stack>
                    {billing.milestones.length === 0 ? (
                        <Typography color="text.secondary" sx={{ paddingY: 1 }}>No milestones yet.</Typography>
                    ) : (
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>Milestone</TableCell>
                                    <TableCell>Due</TableCell>
                                    <TableCell align="right">Amount</TableCell>
                                    <TableCell>Status</TableCell>
                                    {isEditMode && <TableCell />}
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {billing.milestones.map((m) => (
                                    <TableRow key={m.ID} hover>
                                        <TableCell>{m.name}{m.comment && <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>{m.comment}</Typography>}</TableCell>
                                        <TableCell>{ddmmyyyy(m.due_date)}</TableCell>
                                        <TableCell align="right">{formatMoney(m.amount, m.currency)}</TableCell>
                                        <TableCell>
                                            <Select size="small" variant="standard" value={m.status} disabled={!isEditMode}
                                                onChange={(e) => run(() => postData(`projects/${projectId}/billing/milestones/update`, { id: m.ID, status: e.target.value }))}>
                                                <MenuItem value="planned">Planned</MenuItem>
                                                <MenuItem value="achieved">Achieved</MenuItem>
                                                <MenuItem value="invoiced">Invoiced</MenuItem>
                                            </Select>
                                        </TableCell>
                                        {isEditMode && (
                                            <TableCell align="right" sx={{ whiteSpace: 'nowrap' }}>
                                                <IconButton size="small" onClick={() => setMilestoneDialog({
                                                    id: m.ID, name: m.name, amount: m.amount?.toString() ?? '', currency: m.currency ?? currency, due_date: m.due_date ?? '', status: m.status, comment: m.comment ?? '',
                                                })}><Edit fontSize="small" /></IconButton>
                                                <IconButton size="small" color="error" onClick={() => window.confirm(`Remove ${m.name}?`) && run(() => postData(`projects/${projectId}/billing/milestones/remove`, { id: m.ID }))}>
                                                    <Delete fontSize="small" />
                                                </IconButton>
                                            </TableCell>
                                        )}
                                    </TableRow>
                                ))}
                                <TableRow>
                                    <TableCell sx={{ fontWeight: 600 }}>Total</TableCell>
                                    <TableCell />
                                    <TableCell align="right" sx={{ fontWeight: 600 }}>{formatMoney(billing.milestones.reduce((s, m) => s + (m.amount ?? 0), 0), currency)}</TableCell>
                                    <TableCell colSpan={isEditMode ? 2 : 1}>
                                        <Typography variant="caption" color="text.secondary">
                                            {formatMoney(billing.milestones.filter((m) => m.status === 'invoiced').reduce((s, m) => s + (m.amount ?? 0), 0), currency)} invoiced
                                        </Typography>
                                    </TableCell>
                                </TableRow>
                            </TableBody>
                        </Table>
                    )}
                </>
            )}

            {/* Dialogs */}
            <Dialog open={!!periodDialog} onClose={() => setPeriodDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle>{periodDialog?.id ? 'Edit contract period' : 'Add contract period'}</DialogTitle>
                {periodDialog && (
                    <DialogContent>
                        <Stack gap={2} sx={{ paddingTop: 1 }}>
                            <TextField size="small" label="Name" placeholder="8. VE" value={periodDialog.name} onChange={(e) => setPeriodDialog({ ...periodDialog, name: e.target.value })} />
                            <TextField size="small" type="date" label="Start" InputLabelProps={{ shrink: true }} value={periodDialog.start_date} onChange={(e) => setPeriodDialog({ ...periodDialog, start_date: e.target.value })} />
                            <TextField size="small" type="date" label="End" InputLabelProps={{ shrink: true }} value={periodDialog.end_date} onChange={(e) => setPeriodDialog({ ...periodDialog, end_date: e.target.value })} />
                            {!periodDialog.id && billing.periods.length > 0 && (
                                <FormControl size="small">
                                    <InputLabel>Copy positions from</InputLabel>
                                    <Select label="Copy positions from" value={periodDialog.copyFrom} onChange={(e) => setPeriodDialog({ ...periodDialog, copyFrom: e.target.value as number | '' })}>
                                        <MenuItem value="">Start empty</MenuItem>
                                        {billing.periods.map((p) => <MenuItem key={p.ID} value={p.ID}>{p.name}</MenuItem>)}
                                    </Select>
                                </FormControl>
                            )}
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions>
                    {periodDialog?.id && (
                        <Button color="error" sx={{ marginRight: 'auto' }} onClick={() => {
                            if (!window.confirm('Remove this contract period with all its positions and billing periods?')) return;
                            run(() => postData(`projects/${projectId}/billing/periods/remove`, { id: periodDialog.id }), 'Contract period removed.');
                            setPeriodDialog(null);
                        }}>Remove</Button>
                    )}
                    <Button onClick={() => setPeriodDialog(null)}>Cancel</Button>
                    <Button variant="contained" disableElevation disabled={!periodDialog?.name.trim()} onClick={() => {
                        if (!periodDialog) return;
                        const body = { name: periodDialog.name.trim(), start_date: periodDialog.start_date || null, end_date: periodDialog.end_date || null };
                        run(async () => {
                            if (periodDialog.id) {
                                await postData(`projects/${projectId}/billing/periods/update`, { id: periodDialog.id, ...body });
                            } else {
                                const res = await postData(`projects/${projectId}/billing/periods/add`, { ...body, ...(periodDialog.copyFrom ? { copyFromPeriodId: periodDialog.copyFrom } : {}) });
                                setPeriodId(res.id);
                            }
                        }, 'Contract period saved.');
                        setPeriodDialog(null);
                    }}>Save</Button>
                </DialogActions>
            </Dialog>

            <PositionDialog open={!!positionDialog} initial={positionDialog?.form ?? null} onClose={() => setPositionDialog(null)} onSave={savePosition} />

            <MemberDialog open={memberPositionId !== null} onClose={() => setMemberPositionId(null)} onSave={(person) => {
                const positionId = memberPositionId;
                setMemberPositionId(null);
                run(() => postData(`projects/${projectId}/billing/members/add`, {
                    positionId, member_name: person.name, member_email: person.email || null, staff_oid: person.staffOid, expertID: person.expertID,
                }), `${person.name} added.`);
            }} />

            <Dialog open={!!billingPeriodDialog} onClose={() => setBillingPeriodDialog(null)} fullWidth maxWidth="xs">
                <DialogTitle>Add billing period</DialogTitle>
                {billingPeriodDialog && (
                    <DialogContent>
                        <Stack gap={2} sx={{ paddingTop: 1 }}>
                            <TextField size="small" type="date" label="Billing period ends (Abr.per)" InputLabelProps={{ shrink: true }} value={billingPeriodDialog.end_date}
                                onChange={(e) => setBillingPeriodDialog({ ...billingPeriodDialog, end_date: e.target.value })} />
                            <TextField size="small" label="Label (optional)" placeholder="8th invoice" value={billingPeriodDialog.label}
                                onChange={(e) => setBillingPeriodDialog({ ...billingPeriodDialog, label: e.target.value })} />
                        </Stack>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setBillingPeriodDialog(null)}>Cancel</Button>
                    <Button variant="contained" disableElevation disabled={!billingPeriodDialog?.end_date} onClick={() => {
                        if (!billingPeriodDialog || !period) return;
                        run(() => postData(`projects/${projectId}/billing/billing-periods/add`, {
                            contractPeriodId: period.ID, end_date: billingPeriodDialog.end_date, label: billingPeriodDialog.label.trim() || null,
                        }), 'Billing period added.');
                        setBillingPeriodDialog(null);
                    }}>Add</Button>
                </DialogActions>
            </Dialog>

            <QuantitiesDialog target={quantitiesTarget} onClose={() => setQuantitiesTarget(null)} onSave={(q) => {
                if (!quantitiesTarget) return;
                const target = quantitiesTarget;
                setQuantitiesTarget(null);
                run(() => postData(`projects/${projectId}/billing/position-billing/save`, { positionId: target.position.ID, billingPeriodId: target.billingPeriod.ID, ...q }), 'Quantities saved.');
            }} />

            <Dialog open={!!lineDialog} onClose={() => setLineDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>{lineDialog?.id ? 'Edit cost line' : 'Add cost line'}</DialogTitle>
                {lineDialog && (
                    <DialogContent>
                        <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                            <Grid item xs={3}><TextField fullWidth size="small" label="VPos" placeholder="5.8." value={lineDialog.vpos} onChange={(e) => setLineDialog({ ...lineDialog, vpos: e.target.value })} /></Grid>
                            <Grid item xs={9}><TextField fullWidth size="small" required label="Cost line" placeholder="Flights Spalte I (international)" value={lineDialog.label} onChange={(e) => setLineDialog({ ...lineDialog, label: e.target.value })} /></Grid>
                            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Unit price" value={lineDialog.unit_price} onChange={(e) => setLineDialog({ ...lineDialog, unit_price: e.target.value })} /></Grid>
                            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="Planned quantity" value={lineDialog.planned_qty} onChange={(e) => setLineDialog({ ...lineDialog, planned_qty: e.target.value })} /></Grid>
                            <Grid item xs={4}><TextField fullWidth size="small" type="number" label="or planned amount" value={lineDialog.planned_amount} onChange={(e) => setLineDialog({ ...lineDialog, planned_amount: e.target.value })} /></Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary">Unit price x quantity (flights, travel) or a plain amount (office running costs, flexible fee).</Typography>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setLineDialog(null)}>Cancel</Button>
                    <Button variant="contained" disableElevation disabled={!lineDialog?.label.trim()} onClick={() => {
                        if (!lineDialog || !period) return;
                        const body = {
                            vpos: lineDialog.vpos.trim() || null, label: lineDialog.label.trim(), unit_price: numberOrNull(lineDialog.unit_price),
                            planned_qty: numberOrNull(lineDialog.planned_qty), planned_amount: numberOrNull(lineDialog.planned_amount),
                        };
                        run(() => (lineDialog.id
                            ? postData(`projects/${projectId}/billing/budget-lines/update`, { id: lineDialog.id, ...body })
                            : postData(`projects/${projectId}/billing/budget-lines/add`, { contractPeriodId: period.ID, ...body })), 'Cost line saved.');
                        setLineDialog(null);
                    }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!lineBillingTarget} onClose={() => setLineBillingTarget(null)} fullWidth maxWidth="xs">
                <DialogTitle>{lineBillingTarget?.line.label} · Abr.per {ddmmyyyy(lineBillingTarget?.billingPeriod.end_date ?? null)}</DialogTitle>
                {lineBillingTarget && (
                    <DialogContent>
                        <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                            {lineBillingTarget.line.unit_price !== null && (
                                <Grid item xs={6}><TextField fullWidth size="small" type="number" label={`Quantity (x ${lineBillingTarget.line.unit_price})`} value={lineBillingTarget.qty}
                                    onChange={(e) => setLineBillingTarget({ ...lineBillingTarget, qty: e.target.value })} /></Grid>
                            )}
                            <Grid item xs={6}><TextField fullWidth size="small" type="number" label={lineBillingTarget.line.unit_price !== null ? 'or amount' : 'Amount'} value={lineBillingTarget.amount}
                                onChange={(e) => setLineBillingTarget({ ...lineBillingTarget, amount: e.target.value })} /></Grid>
                        </Grid>
                        <Typography variant="caption" color="text.secondary">Leave both empty to remove the entry.</Typography>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setLineBillingTarget(null)}>Cancel</Button>
                    <Button variant="contained" disableElevation onClick={() => {
                        if (!lineBillingTarget) return;
                        const t = lineBillingTarget;
                        setLineBillingTarget(null);
                        run(() => postData(`projects/${projectId}/billing/budget-line-billing/save`, {
                            budgetLineId: t.line.ID, billingPeriodId: t.billingPeriod.ID, qty: numberOrNull(t.qty), amount: numberOrNull(t.amount),
                        }), 'Saved.');
                    }}>Save</Button>
                </DialogActions>
            </Dialog>

            <Dialog open={!!milestoneDialog} onClose={() => setMilestoneDialog(null)} fullWidth maxWidth="sm">
                <DialogTitle>{milestoneDialog?.id ? 'Edit milestone' : 'Add milestone'}</DialogTitle>
                {milestoneDialog && (
                    <DialogContent>
                        <Grid container spacing={2} sx={{ paddingTop: 1 }}>
                            <Grid item xs={12}><TextField fullWidth size="small" required label="Milestone / work package" value={milestoneDialog.name} onChange={(e) => setMilestoneDialog({ ...milestoneDialog, name: e.target.value })} /></Grid>
                            <Grid item xs={5}><TextField fullWidth size="small" type="number" label="Amount" value={milestoneDialog.amount} onChange={(e) => setMilestoneDialog({ ...milestoneDialog, amount: e.target.value })} /></Grid>
                            <Grid item xs={3}>
                                <FormControl fullWidth size="small">
                                    <InputLabel>Currency</InputLabel>
                                    <Select label="Currency" value={milestoneDialog.currency} onChange={(e) => setMilestoneDialog({ ...milestoneDialog, currency: e.target.value })}>
                                        <MenuItem value="EUR">EUR</MenuItem>
                                        <MenuItem value="USD">USD</MenuItem>
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={4}><TextField fullWidth size="small" type="date" label="Due" InputLabelProps={{ shrink: true }} value={milestoneDialog.due_date} onChange={(e) => setMilestoneDialog({ ...milestoneDialog, due_date: e.target.value })} /></Grid>
                            <Grid item xs={12}><TextField fullWidth size="small" label="Comment" value={milestoneDialog.comment} onChange={(e) => setMilestoneDialog({ ...milestoneDialog, comment: e.target.value })} /></Grid>
                        </Grid>
                    </DialogContent>
                )}
                <DialogActions>
                    <Button onClick={() => setMilestoneDialog(null)}>Cancel</Button>
                    <Button variant="contained" disableElevation disabled={!milestoneDialog?.name.trim()} onClick={() => {
                        if (!milestoneDialog) return;
                        const body = {
                            name: milestoneDialog.name.trim(), amount: numberOrNull(milestoneDialog.amount), currency: milestoneDialog.currency || null,
                            due_date: milestoneDialog.due_date || null, status: milestoneDialog.status, comment: milestoneDialog.comment.trim() || null,
                        };
                        run(() => (milestoneDialog.id
                            ? postData(`projects/${projectId}/billing/milestones/update`, { id: milestoneDialog.id, ...body })
                            : postData(`projects/${projectId}/billing/milestones/add`, body)), 'Milestone saved.');
                        setMilestoneDialog(null);
                    }}>Save</Button>
                </DialogActions>
            </Dialog>

            <GizTimesheetDialog open={!!gizDialog} projectId={projectId} initialMemberId={gizDialog?.memberId ?? null} initialMonth={gizDialog?.month ?? null}
                onClose={() => setGizDialog(null)} onSaved={() => load()} />
        </Box>
    );
}

export default ProjectBillingTab;
